/**
 * ChunkErrorBoundary Component
 * MDM-SENTRY-CHUNK-ERROR-20260118
 *
 * Specialized error boundary that detects chunk load failures (dynamic import errors)
 * and attempts to recover by reloading the page with a cache-busting parameter.
 *
 * Features:
 * - Detects ChunkLoadError by error message patterns
 * - Auto-reloads once with cache-bust query param
 * - Prevents infinite reload loops via sessionStorage
 * - Logs to Sentry with chunk-specific context
 * - Shows user-friendly fallback UI if reload fails
 *
 * Usage:
 * <ChunkErrorBoundary>
 *   <Suspense fallback={<Loading />}>
 *     <LazyComponent />
 *   </Suspense>
 * </ChunkErrorBoundary>
 */
import React from 'react';
import { captureException } from '../services/errorTracking';

// Storage key for tracking reload attempts
const CHUNK_RELOAD_KEY = 'mdm_chunk_reload_attempted';

/**
 * Detect if an error is a chunk load failure
 * @param {Error} error - The error to check
 * @returns {boolean} - True if this is a chunk load error
 */
function isChunkLoadError(error) {
  if (!error || !error.message) return false;

  const chunkErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'Loading chunk',
    'ChunkLoadError',
    'Loading CSS chunk',
    'Unable to preload CSS',
    'dynamically imported module',
    'Failed to load module script',
  ];

  return chunkErrorPatterns.some(pattern =>
    error.message.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Check if we've already attempted a reload for chunk errors
 * @returns {boolean} - True if reload was already attempted
 */
function hasAttemptedReload() {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';
  } catch {
    // sessionStorage not available (SSR, privacy mode)
    return false;
  }
}

/**
 * Mark that we've attempted a reload
 */
function markReloadAttempted() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear the reload attempt marker (call on successful load)
 */
export function clearChunkReloadAttempt() {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Perform a cache-busting reload
 */
function performCacheBustReload() {
  markReloadAttempted();

  // Add cache-bust parameter to force fresh assets
  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now().toString());

  // Use location.href for full page reload with cache bust
  window.location.href = url.toString();
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    const isChunk = isChunkLoadError(error);
    return {
      hasError: true,
      error,
      isChunkError: isChunk,
    };
  }

  componentDidCatch(error, errorInfo) {
    const isChunk = isChunkLoadError(error);

    // Log to console for debugging
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);

    // Log to Sentry with chunk-specific context
    captureException(error, errorInfo, {
      source: 'ChunkErrorBoundary',
      isChunkError: isChunk,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // If this is a chunk error and we haven't tried reloading yet, do so
    if (isChunk && !hasAttemptedReload()) {
      console.log('Chunk load error detected. Attempting cache-bust reload...');
      performCacheBustReload();
      return;
    }

    // If we've already tried reloading, show the fallback UI
    if (isChunk && hasAttemptedReload()) {
      console.log('Chunk error persists after reload. Showing fallback UI.');
    }
  }

  handleRetry = () => {
    // Clear the reload marker and try again
    clearChunkReloadAttempt();
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = () => {
    // Clear marker and force a fresh reload
    clearChunkReloadAttempt();
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear marker and navigate home
    clearChunkReloadAttempt();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Allow custom fallback UI via props
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI for chunk errors
      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-zinc-900 rounded-xl border border-zinc-800 p-8 text-center">
            <div className="text-6xl mb-4">🔄</div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {this.state.isChunkError ? 'Update Available' : 'Loading Error'}
            </h1>
            <p className="text-zinc-400 mb-6">
              {this.state.isChunkError
                ? 'A new version of the app is available. Please refresh to get the latest updates.'
                : 'There was a problem loading this page. Please try refreshing.'}
            </p>

            {/* Error details (only in development) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-orange-500 cursor-pointer hover:text-orange-400">
                  Error Details
                </summary>
                <pre className="mt-2 p-3 bg-zinc-800 rounded text-xs text-red-400 overflow-auto max-h-40">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
